import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import { TextSelection } from '@tiptap/pm/state';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { Table, TableRow, TableHeader, TableCell } from '@tiptap/extension-table';
import {
  Bold,
  Heading2,
  Heading3,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Minus,
  Unlink,
} from 'lucide-react';
import type { HandbookChangeRequest, HandbookPolicy } from '../../types/handbookPolicy';
import {
  deleteHandbookChangeRequest,
  submitAndApproveHandbookChangeRequest,
  submitHandbookChangeRequest,
  updateHandbookChangeRequestDraft,
  type HandbookActor,
} from '../../utils/handbookPoliciesFirestore';
import { sanitizeHandbookPolicyHtml } from '../../utils/richComment';
import HandbookChangeRequestDiff from './HandbookChangeRequestDiff';

interface HandbookPolicyEditorProps {
  policy: HandbookPolicy;
  changeRequest: HandbookChangeRequest;
  actor: HandbookActor;
  isCeo: boolean;
  onCancel: () => void;
  onSaved: () => void;
  onDeleted?: () => void;
}

interface HandbookLinkDraft {
  href: string;
  label: string;
}

/** Preserve scope on header/data cells so eligibility charts round-trip. */
const HandbookTableHeader = TableHeader.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      scope: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('scope'),
        renderHTML: (attributes: { scope?: string | null }) =>
          (attributes.scope ? { scope: attributes.scope } : {}),
      },
    };
  },
});

const HandbookTableCell = TableCell.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      scope: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('scope'),
        renderHTML: (attributes: { scope?: string | null }) =>
          (attributes.scope ? { scope: attributes.scope } : {}),
      },
    };
  },
});

function normalizeHandbookHref(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed) || /^mailto:/i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function findLinkRange(editor: Editor): { from: number; to: number; href: string } | null {
  const { state } = editor;
  const linkType = state.schema.marks.link;
  if (!linkType) return null;
  const { from, to } = state.selection;
  const ranges: Array<{ from: number; to: number; href: string }> = [];
  state.doc.descendants((node, pos) => {
    if (!node.isText) return;
    const mark = node.marks.find(item => item.type === linkType);
    if (!mark) return;
    ranges.push({ from: pos, to: pos + node.nodeSize, href: String(mark.attrs.href || '') });
  });
  const hit =
    ranges.find(range => from >= range.from && from <= range.to)
    || ranges.find(range => from < range.to && to > range.from);
  if (!hit) return null;
  let start = hit.from;
  let end = hit.to;
  for (const range of ranges) {
    if (range.href === hit.href && range.from === end) end = range.to;
  }
  for (let i = ranges.length - 1; i >= 0; i -= 1) {
    const range = ranges[i];
    if (range.href === hit.href && range.to === start) start = range.from;
  }
  return { from: start, to: end, href: hit.href };
}

/** Read the link under the caret without dispatching a transaction. */
function readHandbookLink(editor: Editor): HandbookLinkDraft {
  const range = findLinkRange(editor);
  const { from, to } = editor.state.selection;
  const start = range?.from ?? from;
  const end = range?.to ?? to;
  return {
    href: range?.href || String(editor.getAttributes('link').href || ''),
    label: editor.state.doc.textBetween(start, end, ' '),
  };
}

function applyHandbookLink(editor: Editor, href: string, label: string): void {
  const normalized = normalizeHandbookHref(href);
  const linkType = editor.schema.marks.link;
  const text = (label.trim() || normalized).replace(/\n/g, ' ');
  try {
    if (!normalized) {
      const range = findLinkRange(editor);
      if (range) {
        editor.chain().focus().setTextSelection({ from: range.from, to: range.to }).unsetLink().run();
      }
      return;
    }
    if (!linkType || !text) return;

    const range = findLinkRange(editor);
    const { from: selFrom, to: selTo } = editor.state.selection;
    const from = range?.from ?? selFrom;
    const to = range?.to ?? selTo;
    const mark = linkType.create({ href: normalized });
    const node = editor.schema.text(text, [mark]);
    const tr = from === to
      ? editor.state.tr.insert(from, node)
      : editor.state.tr.replaceWith(from, to, node);
    editor.view.dispatch(tr.scrollIntoView());
  } catch (err) {
    console.error('Could not apply handbook link', err);
  }
}

const HandbookLinkPanel: React.FC<{
  draft: HandbookLinkDraft;
  onApply: (next: HandbookLinkDraft) => void;
  onRemove: () => void;
  onClose: () => void;
}> = ({ draft, onApply, onRemove, onClose }) => {
  const [label, setLabel] = useState(draft.label);
  const [url, setUrl] = useState(draft.href);
  const labelRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    labelRef.current?.focus();
    labelRef.current?.select();
  }, []);

  const apply = () => onApply({ href: url, label });

  return (
    <div className="people-handbook-link-panel" role="dialog" aria-label="Edit link">
      <label>
        <span>Display text</span>
        <input
          ref={labelRef}
          className="ops-modal-input people-handbook-editor-input"
          value={label}
          onChange={e => setLabel(e.target.value)}
          placeholder="Link label"
        />
      </label>
      <label>
        <span>URL</span>
        <input
          className="ops-modal-input people-handbook-editor-input"
          value={url}
          onChange={e => setUrl(e.target.value)}
          placeholder="https:// or mailto:"
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              apply();
            }
          }}
        />
      </label>
      <div className="people-handbook-link-panel-actions">
        <button type="button" className="ops-new-btn" onClick={apply}>
          Apply
        </button>
        <button type="button" className="ops-hide-completed-btn people-handbook-delete-btn" onClick={onRemove}>
          Remove
        </button>
        <button type="button" className="ops-hide-completed-btn" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
};

const HandbookPolicyEditor: React.FC<HandbookPolicyEditorProps> = ({
  policy,
  changeRequest,
  actor,
  isCeo,
  onCancel,
  onSaved,
  onDeleted,
}) => {
  const [title, setTitle] = useState(changeRequest.title);
  const [effectiveDate, setEffectiveDate] = useState(changeRequest.effectiveDate);
  const [location, setLocation] = useState(changeRequest.location);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkDraft, setLinkDraft] = useState<HandbookLinkDraft | null>(null);
  const [liveBody, setLiveBody] = useState(changeRequest.body || '');
  const openLinkPanelRef = useRef<() => void>(() => undefined);

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        link: {
          openOnClick: false,
          autolink: true,
          linkOnPaste: true,
          HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
        },
      }),
      Table.configure({
        resizable: false,
        HTMLAttributes: { class: 'people-handbook-table' },
      }),
      TableRow,
      HandbookTableHeader,
      HandbookTableCell,
      Placeholder.configure({ placeholder: 'Write the proposed policy…' }),
    ],
    [],
  );

  const editor = useEditor({
    extensions,
    content: changeRequest.body || '',
    onCreate: ({ editor: ed }) => {
      setLiveBody(ed.getHTML());
    },
    onUpdate: ({ editor: ed }) => {
      setLiveBody(ed.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'people-handbook-editor-content',
        'aria-label': 'Proposed policy body',
      },
      handleClick(view, pos, event) {
        const target = event.target as HTMLElement | null;
        if (!target?.closest?.('a')) return false;
        event.preventDefault();
        try {
          view.dispatch(view.state.tr.setSelection(TextSelection.near(view.state.doc.resolve(pos))));
        } catch {
          // Keep the click from crashing the editor if the position is stale.
        }
        queueMicrotask(() => openLinkPanelRef.current());
        return true;
      },
    },
  });

  const openLinkPanel = () => {
    if (!editor) return;
    setLinkDraft(readHandbookLink(editor));
  };
  openLinkPanelRef.current = openLinkPanel;

  const [, setToolbarTick] = useState(0);
  useEffect(() => {
    if (!editor) return undefined;
    const bump = () => setToolbarTick(n => (n + 1) % 1000);
    editor.on('selectionUpdate', bump);
    return () => {
      editor.off('selectionUpdate', bump);
    };
  }, [editor]);

  const fields = () => ({
    title: title.trim(),
    effectiveDate,
    location,
    body: sanitizeHandbookPolicyHtml(editor?.getHTML() || liveBody),
    bodyFormat: 'html' as const,
  });

  const saveDraft = async () => {
    if (!editor) return;
    const nextTitle = title.trim();
    if (!nextTitle) {
      setError('A policy title is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updateHandbookChangeRequestDraft({
        policyId: policy.id,
        changeRequestId: changeRequest.id,
        current: changeRequest,
        actor,
        isCeo,
        fields: fields(),
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save this draft.');
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    if (!editor) return;
    if (!title.trim()) {
      setError('A policy title is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await submitHandbookChangeRequest({
        policyId: policy.id,
        changeRequest,
        actor,
        isCeo,
        fields: fields(),
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not submit this change request.');
    } finally {
      setSaving(false);
    }
  };

  const publishNow = async () => {
    if (!editor) return;
    if (!title.trim()) {
      setError('A policy title is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await submitAndApproveHandbookChangeRequest({
        policy,
        changeRequest,
        actor,
        isCeo,
        fields: fields(),
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not publish this change.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Delete this change request? This cannot be undone.')) return;
    setSaving(true);
    setError(null);
    try {
      await deleteHandbookChangeRequest({
        policyId: policy.id,
        changeRequest,
        actor,
        isCeo,
      });
      (onDeleted || onSaved)();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete this change request.');
    } finally {
      setSaving(false);
    }
  };

  const proposed = {
    title,
    effectiveDate,
    location,
    body: liveBody,
  };

  return (
    <div className="people-handbook-editor">
      <p className="people-handbook-editor-kicker">
        Change request · base v{changeRequest.baseVersion || '—'}
        {changeRequest.status === 'changes_requested' && changeRequest.reviewNote
          ? ` · CEO note: ${changeRequest.reviewNote}`
          : ''}
      </p>
      <div className="people-handbook-editor-fields">
        <label className="people-handbook-editor-field">
          <span>Title</span>
          <input
            className="ops-modal-input people-handbook-editor-input"
            value={title}
            onChange={e => setTitle(e.target.value)}
            aria-label="Proposed policy title"
          />
        </label>
        <label className="people-handbook-editor-field">
          <span>Effective</span>
          <input
            className="ops-modal-input people-handbook-editor-input"
            value={effectiveDate}
            onChange={e => setEffectiveDate(e.target.value)}
            aria-label="Proposed effective date"
          />
        </label>
        <label className="people-handbook-editor-field people-handbook-editor-field-wide">
          <span>Location</span>
          <input
            className="ops-modal-input people-handbook-editor-input"
            value={location}
            onChange={e => setLocation(e.target.value)}
            aria-label="Proposed policy location"
          />
        </label>
      </div>

      <div className="people-handbook-editor-tc">
        <HandbookChangeRequestDiff proposed={proposed} base={policy} authorName={actor.name} changedOnly />
      </div>

      {editor ? (
        <div className="people-handbook-editor-toolbar" role="toolbar" aria-label="Policy formatting">
          <button type="button" className={editor.isActive('bold') ? 'active' : ''} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold">
            <Bold size={14} />
          </button>
          <button type="button" className={editor.isActive('italic') ? 'active' : ''} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic">
            <Italic size={14} />
          </button>
          <button type="button" className={editor.isActive('heading', { level: 2 }) ? 'active' : ''} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading">
            <Heading2 size={14} />
          </button>
          <button type="button" className={editor.isActive('heading', { level: 3 }) ? 'active' : ''} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Subheading">
            <Heading3 size={14} />
          </button>
          <button type="button" className={editor.isActive('bulletList') ? 'active' : ''} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bulleted list">
            <List size={14} />
          </button>
          <button type="button" className={editor.isActive('orderedList') ? 'active' : ''} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered list">
            <ListOrdered size={14} />
          </button>
          <button
            type="button"
            className={editor.isActive('link') || linkDraft ? 'active' : ''}
            onClick={openLinkPanel}
            title="Insert or edit link"
          >
            <LinkIcon size={14} />
          </button>
          <button
            type="button"
            onClick={() => {
              try {
                const range = findLinkRange(editor);
                if (range) {
                  editor.chain().focus().setTextSelection({ from: range.from, to: range.to }).unsetLink().run();
                }
              } catch (err) {
                console.error('Could not remove handbook link', err);
              }
              setLinkDraft(null);
            }}
            disabled={!editor.isActive('link')}
            title="Remove link"
          >
            <Unlink size={14} />
          </button>
          <button type="button" onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Divider">
            <Minus size={14} />
          </button>
        </div>
      ) : null}

      {linkDraft && editor ? (
        <HandbookLinkPanel
          draft={linkDraft}
          onApply={next => {
            applyHandbookLink(editor, next.href, next.label);
            setLinkDraft(null);
          }}
          onRemove={() => {
            try {
              const range = findLinkRange(editor);
              if (range) {
                editor.chain().focus().setTextSelection({ from: range.from, to: range.to }).unsetLink().run();
              }
            } catch (err) {
              console.error('Could not remove handbook link', err);
            }
            setLinkDraft(null);
          }}
          onClose={() => setLinkDraft(null)}
        />
      ) : null}

      <EditorContent editor={editor} />

      {error ? <p className="people-handbook-editor-error">{error}</p> : null}

      <div className="people-handbook-editor-actions">
        <button type="button" className="ops-hide-completed-btn" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button type="button" className="ops-hide-completed-btn people-handbook-delete-btn" onClick={() => void remove()} disabled={saving}>
          Delete
        </button>
        <button type="button" className="ops-hide-completed-btn" onClick={() => void saveDraft()} disabled={saving}>
          {saving ? 'Saving…' : 'Save draft'}
        </button>
        <button type="button" className="ops-hide-completed-btn active" onClick={() => void submit()} disabled={saving}>
          Submit for review
        </button>
        {isCeo ? (
          <button type="button" className="ops-new-btn" onClick={() => void publishNow()} disabled={saving}>
            Publish now
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default HandbookPolicyEditor;
